import { applyCouponDiscount } from '@/entities/reservation'
import { PLACE_DETAIL_CANONICAL_BASE } from '@/features/seoPlaceDetail/lib/seo'
import {
  type IProduct,
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'

import { type IFaqEntry } from './buildFaqItems'
import { buildOpeningHoursSpecification } from './buildOpeningHoursSpecification'
import { parsePrice } from './parsePrice'
import { ENGINE_OIL_SECTION } from './placeHelpers'
import { sanitizeHttpsUrl, sanitizeTelephone } from './sanitize'

const MYCLE_HOME_URL = 'https://service.mycle.co.kr/v2'
const PLACE_SEARCH_URL = 'https://service.mycle.co.kr/v2/place/search'
const OFFER_CATALOG_NAME = '정비 서비스'
const PRICE_CURRENCY = 'KRW'
const ADDRESS_COUNTRY = 'KR'

interface IBuildAutoRepairJsonLdArgs {
  place: ISeoPlace
  productGroups: IProductGroup[]
  hashId: string
}

type TServiceOffer = {
  '@type': 'Offer'
  itemOffered: {
    '@type': 'Service'
    name: string
  }
  priceSpecification: {
    '@type': 'PriceSpecification'
    minPrice: number
    maxPrice: number
    priceCurrency: string
  }
}

type TOfferCatalog = {
  '@type': 'OfferCatalog'
  name: string
  itemListElement: TServiceOffer[]
}

// priceRange와 hasOfferCatalog가 동일한 가격 소스(group.targetProducts)를 쓰도록 통일
// 엔진오일은 메타 설명·소개글·FAQ와 같은 정가(price) 기준, 그 외 섹션은 쿠폰적용가 기준
const getProductPrice = (group: IProductGroup, product: IProduct) => {
  if (group.section === ENGINE_OIL_SECTION) return parsePrice(product.price)
  return applyCouponDiscount(group.section, parsePrice(product.salePrice))
}

const getGroupPrices = (group: IProductGroup) =>
  group.targetProducts
    .map(product => getProductPrice(group, product))
    .filter(price => price > 0)

const buildServiceOffer = (group: IProductGroup): TServiceOffer | null => {
  const prices = getGroupPrices(group)

  if (prices.length === 0) return null

  return {
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Service',
      name: group.title,
    },
    priceSpecification: {
      '@type': 'PriceSpecification',
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      priceCurrency: PRICE_CURRENCY,
    },
  }
}

const buildOfferCatalog = (
  productGroups: IProductGroup[]
): TOfferCatalog | null => {
  const itemListElement = productGroups
    .map(buildServiceOffer)
    .filter((offer): offer is TServiceOffer => offer !== null)

  if (itemListElement.length === 0) return null

  return {
    '@type': 'OfferCatalog',
    name: OFFER_CATALOG_NAME,
    itemListElement,
  }
}

export const buildAutoRepairJsonLd = ({
  place,
  productGroups,
  hashId,
}: IBuildAutoRepairJsonLdArgs) => {
  const canonical = `${PLACE_DETAIL_CANONICAL_BASE}/${encodeURIComponent(hashId)}`
  const image = sanitizeHttpsUrl(place.images?.[0]?.url || place.logoImage?.url)
  const telephone = sanitizeTelephone(place.telephone)
  const streetAddress =
    place.coordinates?.streetAddress || place.coordinates?.address || ''

  const prices = productGroups.flatMap(getGroupPrices)
  const priceRange =
    prices.length > 0
      ? `₩${Math.min(...prices).toLocaleString()}~₩${Math.max(...prices).toLocaleString()}`
      : null

  const offerCatalog = buildOfferCatalog(productGroups)
  const openingHoursSpecification = buildOpeningHoursSpecification(
    place.businessHours
  )

  return {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    name: place.placeName,
    url: canonical,
    ...(image && { image }),
    ...(telephone && { telephone }),
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality: place.coordinates?.city,
      addressRegion: place.coordinates?.state,
      addressCountry: ADDRESS_COUNTRY,
      ...(place.coordinates?.zipCode && {
        postalCode: place.coordinates.zipCode,
      }),
    },
    ...(place.coordinates?.latitude != null &&
      place.coordinates?.longitude != null && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: place.coordinates.latitude,
          longitude: place.coordinates.longitude,
        },
      }),
    ...(priceRange && { priceRange }),
    ...(offerCatalog && { hasOfferCatalog: offerCatalog }),
    ...(openingHoursSpecification.length > 0 && {
      openingHoursSpecification,
    }),
  }
}

interface IBuildBreadcrumbJsonLdArgs {
  place: ISeoPlace
  hashId: string
}

export const buildBreadcrumbJsonLd = ({
  place,
  hashId,
}: IBuildBreadcrumbJsonLdArgs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: '마이클',
      item: MYCLE_HOME_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: '정비소 찾기',
      item: PLACE_SEARCH_URL,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: place.placeName,
      item: `${PLACE_DETAIL_CANONICAL_BASE}/${encodeURIComponent(hashId)}`,
    },
  ],
})

export const buildFaqJsonLd = (faqItems: IFaqEntry[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
})
